// MatrixCell — Eine einzelne Zelle in der Monatsmatrix
// Unterstützt STRG/⌘-Drag&Drop zum Kopieren einer Funktion in eine andere Zelle.
import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import WunschHoverCard from './WunschHoverCard';
import {
  extractFahrzeugNummer, STATUS_CODES,
  getFahrzeugFarbe, getKontrastFarbe,
} from './constants';
import { getMatrixCellSegments } from './matrixLayout';

// MIME-Type für unseren Drag-Payload (verhindert Konflikte mit anderen Drag-Sources)
const DRAG_MIME = 'application/x-einteilung-funktion';

export function MatrixCell({
  mitarbeiter, diensttag, einteilung, onCellClick, canEdit,
  fahrzeugTypNummern, fahrzeuge,
  // Neu: optionaler Callback für Drag&Drop-Kopie
  onCellCopy,
  // Wunsch-Daten
  wunsch,            // optional: { id, fahrzeug_name, notiz, status, ... }
  onWunschOpen,      // optional: (mitarbeiter, diensttag, wunsch) => void
  canSeeWunschHover, // boolean — Einteiler darf Tooltip sehen
  currentUserId,     // für isOwner-Check (View-Only-User: nur eigene Zelle erlaubt)
  // Multi-Select
  isSelected = false,          // boolean: Zelle Teil der aktuellen Bulk-Auswahl
  onMultiSelect,               // optional: (mitarbeiter, diensttag, event) => void
  // Drag-to-Select (Maus gedrückt halten + über Zellen ziehen)
  onDragSelectStart,           // (mitarbeiter, diensttag, event) => void
  onDragSelectEnter,           // (mitarbeiter, diensttag) => void
  wasDragSelectingRef,         // ref: falls true → onClick unterdrücken
}) {
  const data = einteilung || null;
  const [dropActive, setDropActive] = useState(false);

  const getStatusCode = () => {
    if (!data) return null;
    if (data.ist_krank) return 'K';
    if (data.ist_urlaub) return 'SU';
    if (data.ist_wandern && data.bemerkung === 'RTW Wandern') return 'RW';
    if (data.ist_wandern) return 'WA';
    if (data.ist_tausch) return 'T';
    if (data.ist_frei && data.bemerkung === 'Fortbildung') return 'F';
    if (data.ist_frei && data.bemerkung === 'ZBV') return 'ZBV';
    if (data.ist_frei) return '--';
    return null;
  };

  const statusCode = getStatusCode();
  const hauptfunktion = data?.hauptfunktion;
  const sprungfunktionen = data?.sprungfunktionen || [];

  // Fahrzeug-Lookup: id → fahrzeug-Objekt
  const fzById = React.useMemo(() => {
    const m = {};
    (fahrzeuge || []).forEach(f => { m[f.id] = f; });
    return m;
  }, [fahrzeuge]);

  const getFunktionStyle = (funktion) => {
    if (!funktion) return { backgroundColor: '#64748b', color: '#ffffff' };
    if ((funktion.funktion_kuerzel || '') === 'ZUS') {
      return {
        backgroundColor: '#fde68a',
        color: '#78350f',
        border: '1px dashed #d97706',
      };
    }
    const fz = fzById[funktion.fahrzeug_id];
    const bg = getFahrzeugFarbe(fz);
    return { backgroundColor: bg, color: getKontrastFarbe(bg) };
  };

  const getDisplayKuerzel = (funktion) => {
    if (!funktion) return '';
    const kuerzel = funktion.funktion_kuerzel || '';
    if (kuerzel === 'ZUS') {
      const fahrzeugName = funktion.fahrzeug_name || '';
      const fzKurz = fahrzeugName.split(' ')[0] || 'FZ';
      return `+${fzKurz}`;
    }
    // Wenn das Kürzel selbst bereits mit einer Ziffer endet (z. B. 'RTW1B',
    // 'MZF2A'), enthält es die Fahrzeug-Nummer schon → NICHT nochmal anhängen.
    if (/\d$/.test(kuerzel)) return kuerzel;
    const fzNummer = fahrzeugTypNummern?.[funktion.fahrzeug_id]
      ?? extractFahrzeugNummer(funktion.fahrzeug_name || '');
    return fzNummer ? `${kuerzel}${fzNummer}` : kuerzel;
  };

  const getSpringerDisplayName = (sf) => {
    if (!sf) return '';
    const fahrzeugName = sf.fahrzeug_name || '';
    const parts = fahrzeugName.split(' ');
    if (parts.length >= 2) {
      const typ = parts[0];
      const rest = parts.slice(1).join(' ');
      if (rest.includes('/X') || rest.includes('X')) return `${typ} X`;
      return typ;
    }
    return fahrzeugName.split(' ')[0] || sf.funktion_kuerzel || '';
  };

  const getFullSpringerDescription = () => {
    if (!sprungfunktionen.length) return '';
    return sprungfunktionen
      .map(sf => `${sf.funktion_kuerzel} ${getSpringerDisplayName(sf)}`)
      .join(', ');
  };

  const handleClick = (e) => {
    // Unterdrücken, wenn gerade eine Drag-Select-Bewegung endete
    if (wasDragSelectingRef?.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // Multi-Select: Ctrl/Cmd/Shift+Klick → nicht öffnen, sondern nur markieren
    if (canEdit && onMultiSelect && (e.ctrlKey || e.metaKey || e.shiftKey)) {
      e.preventDefault();
      onMultiSelect(mitarbeiter, diensttag, e);
      return;
    }
    // Wenn Edit-Recht: normales Einteilungs-Modal
    if (canEdit) {
      onCellClick(mitarbeiter, diensttag, data);
      return;
    }
    // Ohne Edit-Recht: Wunsch-Modal — nur für eigene Zelle erlaubt
    if (!onWunschOpen) return;
    const isOwnCell = currentUserId && currentUserId === mitarbeiter.id;
    if (!isOwnCell) {
      toast.info('Wünsche kannst du nur für deine eigenen Diensttage eintragen.');
      return;
    }
    onWunschOpen(mitarbeiter, diensttag, wunsch);
  };

  const handleMouseDown = (e) => {
    // Nur linke Maustaste, Edit-Recht, keine Modifier (die gehen an handleClick)
    if (!canEdit || !onDragSelectStart) return;
    if (e.button !== 0) return;
    if (e.ctrlKey || e.metaKey || e.shiftKey) return;
    onDragSelectStart(mitarbeiter, diensttag, e);
  };

  const handleMouseEnter = () => {
    if (!canEdit || !onDragSelectEnter) return;
    onDragSelectEnter(mitarbeiter, diensttag);
  };

  const handleContextMenu = (e) => {
    // Rechtsklick = Wunsch-Modal (für Edit-User auf jede Zelle, sonst nur eigene)
    if (!onWunschOpen) return;
    e.preventDefault();
    if (!canEdit) {
      const isOwnCell = currentUserId && currentUserId === mitarbeiter.id;
      if (!isOwnCell) return;
    }
    onWunschOpen(mitarbeiter, diensttag, wunsch);
  };

  const hasWunsch = !!wunsch;

  const hasSpringer = sprungfunktionen.length > 0;
  const fullDescription = getFullSpringerDescription();
  const compactSegments = getMatrixCellSegments(data, fahrzeugTypNummern);

  // Aushilfe-Anzeige: Wenn ein realer Mitarbeiter für einen Gast-Slot eingetragen ist
  const aushilfeUserName = data?.aushilfe_user_name || null;
  const aushilfeAbteilung = data?.aushilfe_abteilung_name || null;
  const aushilfeInitials = aushilfeUserName
    ? aushilfeUserName.split(' ').map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
    : null;

  // === Drag&Drop: STRG/⌘ + Drag zum Kopieren =====================
  // Source = Zellen mit Hauptfunktion ODER Status (K/SU/WA/F/--)
  const canDrag = !!canEdit && (!!hauptfunktion || !!statusCode);
  // Drop-Ziel = jede editierbare Zelle (überschreibt vorhandenen Inhalt)
  const canDrop = !!canEdit;

  const handleDragStart = (e) => {
    if (!canDrag) {
      e.preventDefault();
      return;
    }
    let payload;
    if (statusCode) {
      payload = {
        kind: 'status',
        mitarbeiter_id: mitarbeiter.id,
        datum: diensttag.datum,
        dienst_art: diensttag.dienst_art,
        status_code: statusCode,
      };
    } else {
      payload = {
        kind: 'funktion',
        mitarbeiter_id: mitarbeiter.id,
        datum: diensttag.datum,
        dienst_art: diensttag.dienst_art,
        fahrzeug_id: hauptfunktion.fahrzeug_id,
        funktion_kuerzel: hauptfunktion.funktion_kuerzel,
        ist_praktikant: !!data?.ist_praktikant,
        // Beim Kopieren/Umsortieren eines Gast-Slots muss die verknüpfte
        // Aushilfe aus der anderen Wachabteilung mitwandern.
        aushilfe_user_id: data?.aushilfe_user_id || null,
        springer_funktionen: sprungfunktionen.map(sf => ({
          fahrzeug_id: sf.fahrzeug_id,
          funktion_kuerzel: sf.funktion_kuerzel,
        })),
      };
    }
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    if (!canDrop) return;
    // Nur reagieren wenn unser MIME-Type vorhanden ist
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = (e.ctrlKey || e.metaKey) ? 'copy' : 'none';
    if (!dropActive) setDropActive(true);
  };

  const handleDragLeave = () => {
    if (dropActive) setDropActive(false);
  };

  const handleDrop = (e) => {
    setDropActive(false);
    if (!canDrop) return;
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (!raw) return;
    e.preventDefault();
    e.stopPropagation();
    let payload;
    try { payload = JSON.parse(raw); } catch { return; }
    const target = {
      mitarbeiter_id: mitarbeiter.id,
      datum: diensttag.datum,
      dienst_art: diensttag.dienst_art,
    };
    const isCopy = !!(e.ctrlKey || e.metaKey);
    if (onCellCopy) onCellCopy(payload, target, isCopy);
  };

  const cellButton = (
    <button
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      disabled={!canEdit && !onWunschOpen}
      draggable={canDrag}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid={`matrix-cell-${mitarbeiter?.id}-${diensttag?.datum}-${diensttag?.dienst_art}`}
      title={
        aushilfeUserName
          ? `Aushilfe: ${aushilfeUserName}${aushilfeAbteilung ? ` (${aushilfeAbteilung})` : ''}${hauptfunktion ? ` — ${getDisplayKuerzel(hauptfunktion)}` : ''}${hasSpringer ? ` + ${fullDescription}` : ''}`
          : (hasSpringer && !hasWunsch ? `${getDisplayKuerzel(hauptfunktion)} + ${fullDescription}` : undefined)
      }
      className={cn(
        'relative w-full h-full min-h-[34px] p-0.5 border-r border-b transition-all duration-150',
        (canEdit || onWunschOpen) && 'hover:bg-primary/10 hover:scale-[1.02] cursor-pointer',
        !canEdit && !onWunschOpen && 'cursor-default',
        diensttag.dienst_art === 'N' && 'bg-slate-50/50',
        dropActive && 'ring-2 ring-orange-400 ring-inset bg-orange-50/60',
        hasWunsch && wunsch.status !== 'ABGELEHNT' && 'ring-2 ring-orange-300 ring-inset',
        hasWunsch && wunsch.status === 'ABGELEHNT' && 'ring-2 ring-stone-300 ring-inset opacity-90',
        isSelected && 'ring-2 ring-sky-500 ring-inset bg-sky-100/60'
      )}
    >
      {hasWunsch && (
        <Sparkles
          className={cn(
            'absolute top-0.5 right-0.5 w-2.5 h-2.5 z-10',
            wunsch.status === 'ABGELEHNT' ? 'text-stone-500' : 'text-orange-500'
          )}
        />
      )}
      {aushilfeInitials && (
        <div
          className="absolute top-0 left-0 z-10 px-1 py-[1px] rounded-br bg-violet-600 text-white text-[8px] font-bold leading-tight shadow-sm"
          data-testid={`aushilfe-badge-${mitarbeiter?.id}-${diensttag?.datum}-${diensttag?.dienst_art}`}
        >
          {aushilfeInitials}
        </div>
      )}
      {statusCode ? (
        <div className={cn(
          'w-full h-full flex items-center justify-center rounded text-sm font-bold',
          STATUS_CODES[statusCode].bgLight,
          STATUS_CODES[statusCode].textColor
        )}>
          {STATUS_CODES[statusCode].displayCode || statusCode}
        </div>
      ) : hauptfunktion ? (
        <div className="w-full h-full flex flex-row gap-0.5 p-0.5">
          {compactSegments.map((segment, i) => (
            <div
              key={`${segment.kind}-${segment.funktion?.fahrzeug_id || 'fz'}-${segment.funktion?.funktion_kuerzel || i}-${i}`}
              className={cn(
                'min-w-0 flex-1 rounded font-bold flex items-center justify-center px-1 leading-tight text-center whitespace-nowrap',
                segment.label.length > 4 ? 'text-[9px]' : 'text-[10px] sm:text-xs'
              )}
              style={{
                ...getFunktionStyle(segment.funktion),
                ...(segment.kind === 'springer'
                  ? { boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.4)' }
                  : {}),
              }}
            >
              {segment.label}
            </div>
          ))}
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted-foreground/20">
          •
        </div>
      )}
    </button>
  );

  // Falls Wunsch existiert und Hover erlaubt → Cell in HoverCard wickeln
  if (hasWunsch && canSeeWunschHover) {
    const isOwner = currentUserId && currentUserId === mitarbeiter.id;
    const canEditWunsch = canEdit || isOwner;
    return (
      <WunschHoverCard
        wunsch={wunsch}
        canEditWunsch={canEditWunsch}
        openModal={onWunschOpen ? () => onWunschOpen(mitarbeiter, diensttag, wunsch) : null}
      >
        {cellButton}
      </WunschHoverCard>
    );
  }
  return cellButton;
}

export default MatrixCell;
